import { useQuery } from '@apollo/client'
import { PublisherInterface } from '@chili-publish/publisher-interface'
import config from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import useAuth from 'data/gradoo/hooks/useAuth'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { GET_PROJECT_COVER_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import CoverStartEditingPanel from 'screens/YearbookCoverEditor/components/CoverStartEditingPanel'
import CustomCoverEditingPanel from 'screens/YearbookCoverEditor/components/CustomCoverEditingPanel'
import CustomCoverPreview from 'screens/YearbookCoverEditor/components/CustomCoverPreview'
import EditorIframe from 'shared/components/EditorIframe'
import Tools from 'shared/components/Tools'
import {
  EDITOR_PANEL_SIDE_MARGIN,
  EDITOR_PANEL_WIDTH
} from 'shared/consts'
import { useChiliApisInit } from 'shared/hooks/useChiliApisInit'
import useDisablePinchZoom from 'shared/hooks/useDisablePinchZoom'
import { useEditorSave } from 'shared/hooks/useEditorSave'
import { useEditorZoom } from 'shared/hooks/useEditorZoom'
import { useLiveUsers } from 'shared/hooks/useLiveUsers'
import AdminLayout from 'shared/layout/Admin'
import { CoverSides } from 'shared/types/global'
import IcomoonIcon from 'shared/ui/IcomoonIcon'
import Selector from 'shared/ui/Selector'

import AdminProjectCoverEditorHeader from './components/Header'

const BottomActions = styled.div`
  position: absolute;
  bottom: 24px;
  margin-bottom: 4px;
  right: 50%;
  transform: translateX(50%);
  display: flex;
  gap: 8px;
  min-width: 260px;
`

const ZoomWrapper = styled.div`
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.05);
  padding: 4px 8px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.color.base.c2};
`

const ZoomText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.base.c6};
  min-width: 30px;
  text-align: center;
`

const MakeFitZoomButton = styled.button`
  color: ${({ theme }) => theme.color.brand_02};
  border: none;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  background: transparent;
  cursor: pointer;
`

const ZoomIconButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  padding: 0px 8px;

  &:hover {
    color: ${({ theme }) => theme.color.brand_02};
  }
`

const MoveIconButton = styled.button<{ isActive: boolean }>`
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.color.brand_02 : theme.color.base.c2};
  border-radius: 100px;
  padding: 4px 16px;
  display: flex;
  align-items: center;
  color: ${({ theme, isActive }) =>
    isActive ? theme.color.brand_02 : theme.color.base.c10};
  &:hover {
    color: ${({ theme }) => theme.color.brand_02};
  }
`

const CoverSideSwitchContainer = styled.div`
  position: absolute;
  top: 50px;
  padding-left: ${EDITOR_PANEL_SIDE_MARGIN + EDITOR_PANEL_WIDTH}px;
  left: 50%;
  transform: translateX(-50%);
`

const getCurrentDocumentId = (
  coverSide: CoverSides,
  documents?: any[]
): null => {
  if (!documents) {
    return null
  }

  return documents.find(doc => doc.name.includes(coverSide))?.id
}

const WORKSPACE_ID = config.chiliWorkspaceId
const SCREEN_HEIGHT = window.innerHeight
const SCREEN_WIDTH = window.innerWidth
// TODO: check if this can be sovled by changing zoom logic
const INITIAL_ZOOM = Math.round(SCREEN_HEIGHT / 12 / 10) * 10 // this is the ratio I discovered to workaround zoom inconsistency for cover

const AdminProjectCoverEditor: React.FC = () => {
  useDisablePinchZoom()
  const liveUsers = useLiveUsers()

  const { pathname } = useLocation()
  const coverSide = pathname.endsWith('/outside')
    ? CoverSides.outside
    : CoverSides.inside

  const navigate = useNavigate()

  const { groupId, projectId } = useParams<{
    groupId: string
    projectId: string
  }>()
  const { authUserGroup } = useAuth()

  const [publisherConnector, setPublisherConnector] =
    useState<PublisherInterface | null>(null)
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
  const [isWorkspaceRendered, setIsWorkspaceRendered] =
    useState(false)
  const [isSwitchingCover, setIsSwitchingCover] = useState(false)

  const { data: projectMetaData, loading: loadingProjectMeta } =
    useQuery(GET_PROJECT_META, {
      variables: {
        projectId: projectId || ''
      },
      skip: !projectId
    })

  const { data: coverDocumentsData, loading: loadingCoverDocuments } =
    useQuery(GET_PROJECT_COVER_DOCUMENTS, {
      variables: {
        groupId: groupId || '',
        projectId: projectId || ''
      },
      skip: !groupId || !projectId
    })

  const { data: checkForCustomCoverData } = useQuery(
    CHECK_FOR_CUSTOM_COVER,
    {
      variables: {
        projectId: projectId!
      }
    }
  )
  const withCustomCover =
    !!checkForCustomCoverData?.checkForCustomCover?.[coverSide]

  const isSoftCover = useMemo(() => {
    return projectMetaData?.getProjectMeta.coverType === 'soft'
  }, [projectMetaData])

  const documentId = useMemo(() => {
    const documents = coverDocumentsData?.findProjectCoverDocuments

    const result = getCurrentDocumentId(coverSide, documents)

    if (!result && documents) {
      navigate(-1)
      throw new Error(`Cover page isn't found`)
    }

    return result
  }, [coverSide, coverDocumentsData])

  useChiliApisInit({
    publisherConnector: publisherConnector!,
    isDocumentLoaded
  })

  useEffect(() => {
    setIsSwitchingCover(true)
    setPublisherConnector(null)
    setIsDocumentLoaded(false)
    setIsWorkspaceRendered(false)
  }, [coverSide])

  useEffect(() => {
    if (isDocumentLoaded || withCustomCover) {
      setIsSwitchingCover(false)
    }
  }, [isDocumentLoaded, withCustomCover])

  const {
    onZoomFit,
    onZoomIn,
    onZoomOut,
    onMoveToggle,
    initialZoom,
    currentZoomLabel,
    currentZoomValue,
    cursorType
  } = useEditorZoom({
    publisherConnector: publisherConnector!,
    isWorkspaceRendered,
    initialZoomValue: INITIAL_ZOOM
  })

  const { handleSave, isLoading: isDocumentSaveLoading } =
    useEditorSave({
      publisherConnector,
      backPath: `/admin/projects/${groupId}/${projectId}/cover`,
      groupId: groupId!,
      projectId: projectId!,
      pageId: documentId!
    })

  const setCanvasMargins = async () => {
    const HEADER_HEIGHT = 62

    const calculateLeftPageMargin = () => {
      const leftPanelIndent =
        EDITOR_PANEL_SIDE_MARGIN + EDITOR_PANEL_WIDTH

      return (SCREEN_WIDTH - leftPanelIndent) / 3
    }

    const calculatePageFitMarginX = () => {
      return SCREEN_WIDTH / 4
    }

    const calculatePageCanvasMarginY = () => {
      return HEADER_HEIGHT + 78
    }

    const pageFitMarginX = calculatePageFitMarginX()
    const pageFitMarginY = 90
    const pageCanvasMarginX = calculateLeftPageMargin()
    const pageCanvasMarginY = calculatePageCanvasMarginY()

    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageCanvasMarginX',
      pageCanvasMarginX
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageCanvasMarginY',
      pageCanvasMarginY
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageFitMarginY',
      pageFitMarginY
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageFitMarginX',
      pageFitMarginX
    )
  }

  useEffect(() => {
    if (!publisherConnector && !isDocumentLoaded) {
      return
    }

    setCanvasMargins()
  }, [publisherConnector, isDocumentLoaded])

  return (
    <AdminLayout
      showFullscreenButton={false}
      renderHeaderContent={() => (
        <AdminProjectCoverEditorHeader
          liveUsers={liveUsers}
          currentView="cover"
          onSaveAndLeave={handleSave}
          isSaveLoading={isDocumentSaveLoading}
          tools={
            <Tools
              publisherConnector={publisherConnector!}
              isDocumentLoaded={isDocumentLoaded}
              showRightPanel={false}
              showLeftPanel={withCustomCover ? true : !isSwitchingCover}
              renderDefaultLeftPanel={() =>
                withCustomCover ? (
                  <CustomCoverEditingPanel coverSide={coverSide} />
                ) : (
                  <CoverStartEditingPanel
                    withCustomCover={withCustomCover}
                    coverSide={coverSide}
                  />
                )
              }
            />
          }
        />
      )}
    >
      {isSoftCover && (
        <CoverSideSwitchContainer>
          <Selector options={['inside', 'outside']} />
        </CoverSideSwitchContainer>
      )}

      {withCustomCover ? (
        <CustomCoverPreview side={coverSide} />
      ) : (
        <EditorIframe
          pageId={documentId!}
          workspaceId={WORKSPACE_ID}
          onBuild={publisherConnector => {
            setPublisherConnector(publisherConnector)
          }}
          onDocumentLoad={() => {
            setIsDocumentLoaded(true)
          }}
          onWorkspaceRender={() => setIsWorkspaceRendered(true)}
        />
      )}

      {withCustomCover ? (
        <></>
      ) : (
        <BottomActions>
          <MoveIconButton
            isActive={cursorType === 'hand'}
            onClick={onMoveToggle}
          >
            <IcomoonIcon size={24} icon="move" />
          </MoveIconButton>
          {initialZoom.current && (
            <ZoomWrapper>
              <ZoomIconButton onClick={onZoomOut}>
                <IcomoonIcon size={24} icon="zoom-out" />
              </ZoomIconButton>

              <ZoomText>{currentZoomLabel}%</ZoomText>

              <ZoomIconButton onClick={onZoomIn}>
                <IcomoonIcon size={24} icon="zoom-in" />
              </ZoomIconButton>
              {initialZoom.current !== currentZoomValue && (
                <MakeFitZoomButton onClick={onZoomFit}>
                  Make fit
                </MakeFitZoomButton>
              )}
            </ZoomWrapper>
          )}
        </BottomActions>
      )}
    </AdminLayout>
  )
}

export default AdminProjectCoverEditor
